import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Users } from 'lucide-react'
import type { Account } from '@limo/database'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { relativeTime } from './ottoPortal'

interface LocationUser {
  brUserId: string
  email: string
  connectedSince: string
  grants: number
  lastActivityAt: string | null
}

interface LocationUsersResponse {
  locationId: string
  users: LocationUser[]
}

interface DisconnectResponse {
  revokedFamilies: number
}

const DISCONNECT_ERROR_TEXT: Record<string, string> = {
  forbidden: 'Only platform admins can disconnect another team member.',
}

async function invokeOttoPortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('miles-portal', { body })
  if (error) {
    const context = (error as { context?: Response }).context
    const detail = context ? await context.json().catch(() => null) : null
    throw new Error(detail?.error ?? 'unavailable')
  }
  return data as T
}

function fetchLocationUsers(accountId?: string): Promise<LocationUsersResponse> {
  return invokeOttoPortal(accountId ? { action: 'location_users', accountId } : { action: 'location_users' })
}

// Admin-only: revoke ONE person's Claude grants on this account's location.
// The edge function enforces the admin role; the Otto side scopes the revoke
// to (user, location), so nothing else is touched.
function disconnectMember(accountId: string, brUserId: string): Promise<DisconnectResponse> {
  return invokeOttoPortal({ action: 'disconnect', accountId, brUserId })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// Everyone whose Claude is currently connected to this account's GHL
// location. Read-only for members — each person revokes their own grant from
// their own page. In the admin (impersonation) view each row gets a
// Disconnect action, so Limo Marketer can sign someone out of Otto without
// resetting the whole account.
export function OttoLocationUsersCard({ adminAccount }: { adminAccount?: Account }) {
  const { user, account: ownAccount } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const account = adminAccount ?? ownAccount
  const [pending, setPending] = useState<LocationUser | null>(null)

  const usersQuery = useQuery({
    queryKey: ['otto-location-users', account?.id],
    queryFn: () => fetchLocationUsers(adminAccount?.id),
    enabled: !!account?.id,
    refetchInterval: 60_000,
    retry: 1,
  })

  const disconnectMutation = useMutation({
    mutationFn: (member: LocationUser) => {
      if (!adminAccount) throw new Error('forbidden')
      return disconnectMember(adminAccount.id, member.brUserId)
    },
    onSuccess: (result, member) => {
      toast({
        title: result.revokedFamilies > 0 ? 'Claude disconnected' : 'Nothing was connected',
        description:
          result.revokedFamilies > 0
            ? `${member.email} has been signed out of Otto. They can reconnect anytime from Claude.`
            : `${member.email} no longer had a live Claude connection.`,
      })
      queryClient.invalidateQueries({ queryKey: ['otto-location-users', account?.id] })
      queryClient.invalidateQueries({ queryKey: ['otto-status', account?.id] })
      setPending(null)
    },
    onError: (error: Error) =>
      toast({
        title: 'Could not disconnect',
        description:
          DISCONNECT_ERROR_TEXT[error.message] ?? 'Otto is temporarily unavailable. Nothing was changed.',
        variant: 'destructive',
      }),
  })

  const mappingMissing = usersQuery.error?.message === 'no_location_mapping'
  // Setup problems are already explained by the Status card above.
  if (mappingMissing) return null

  const users = usersQuery.data?.users ?? []
  const canDisconnect = !!adminAccount

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Connected team members
          {usersQuery.data && <Badge variant="secondary">{users.length}</Badge>}
        </CardTitle>
        <CardDescription>
          {usersQuery.isError ? (
            <>
              This list is temporarily unavailable.{' '}
              <button className="underline" onClick={() => usersQuery.refetch()}>
                Retry
              </button>
            </>
          ) : (
            'Everyone whose Claude is signed in to this location right now.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {usersQuery.isLoading ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        ) : usersQuery.data && users.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody is connected yet. Follow the steps below to connect Claude.
          </p>
        ) : users.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Connected since</TableHead>
                <TableHead>Last activity</TableHead>
                {canDisconnect && <TableHead className="w-px" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((member) => (
                <TableRow key={member.brUserId}>
                  <TableCell className="font-medium">
                    {member.email}
                    {member.brUserId === user?.id && (
                      <Badge variant="outline" className="ml-2">
                        You
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(member.connectedSince)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {member.lastActivityAt ? relativeTime(member.lastActivityAt) : 'No activity yet'}
                  </TableCell>
                  {canDisconnect && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={disconnectMutation.isPending}
                        onClick={() => setPending(member)}
                      >
                        Disconnect
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        {canDisconnect && (
          <AlertDialog
            open={pending !== null}
            onOpenChange={(open) => !open && !disconnectMutation.isPending && setPending(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect {pending?.email}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This signs their Claude out of Otto for {adminAccount?.name}. They can reconnect anytime by
                  signing in again from Claude. Nothing changes in Booked Rides or GoHighLevel.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={disconnectMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={disconnectMutation.isPending}
                  onClick={(e) => {
                    e.preventDefault()
                    if (pending) disconnectMutation.mutate(pending)
                  }}
                >
                  {disconnectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  )
}
